import { Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Image01Icon } from '@vizit/icons';
import { Asset, LibraryEmptyState } from '@vizit/molecules';
import { cva, cx } from '@vizit/theme';
import React from 'react';

interface AssetGridProps {
  /**
   * Asset components to display in the grid
   * @type {React.ReactNode}
   * @required false
   */
  children?: React.ReactNode;
  /**
   * Total number of assets (for display text)
   * @type {number}
   * @required true
   */
  totalCount: number;
  /**
   * Upload asset dialog to display in the empty state
   * @type {React.ReactNode}
   * @required false
   */
  uploadAssetDialog?: React.ReactNode;
  /**
   * Whether to show loading state
   * @type {boolean}
   * @required false
   * @default false
   */
  isLoading?: boolean;
  /**
   * Custom CSS class for the grid container
   * @type {string}
   * @required false
   */
  className?: string;
  /**
   * Custom text to display before the count (e.g., "Showing", "Found")
   * @type {string}
   * @required false
   * @default ""
   */
  countPrefix?: string;
  /**
   * Number of loading assets to display
   * @type {number}
   * @required false
   * @default 12
   */
  loadingCount?: number;
  /**
   * Whether to show the count
   * @type {boolean}
   * @required false
   * @default true
   */
  showCount?: boolean;
  /**
   * Fixed number of columns, e.g. from a thumbnail size control. Leave it out to keep the responsive columns.
   * @type {number}
   * @required false
   */
  columns?: number;
}

/**
 * AssetGrid displays a responsive grid of asset cards with a total count header.
 * Designed for library/gallery views where assets are displayed without score badges.
 *
 * Features:
 * - Responsive grid layout that adapts to viewport size
 * - Asset count display at the top
 * - Accepts Asset components as children for maximum flexibility
 * - Loading states with skeleton placeholders
 *
 * @example
 * <AssetGrid totalCount={31}>
 *   {assets.map(asset => (
 *     <Asset key={asset.id} {...asset} showScoreBar={false}>
 *       <Asset.Image src={asset.url} name={asset.name} />
 *     </Asset>
 *   ))}
 * </AssetGrid>
 */

const containerClasses = cva({
  base: 'space-y-6',
});

const gridClasses = cva({
  base: 'grid gap-8',
  variants: {
    isResponsive: {
      // Responsive columns - starts with 2 columns on mobile, scales up. Keep in sync with `getResponsiveGridColumns`
      // in `@vizit/utils`, which estimates the first page size from these columns.
      true: [
        'grid-cols-2', // Mobile: 2 columns
        'sm:grid-cols-3', // Small: 3 columns (640px+)
        'md:grid-cols-4', // Medium: 4 columns (768px+)
        'lg:grid-cols-5', // Large: 5 columns (1024px+)
        'xl:grid-cols-6', // Extra large: 6 columns (1280px+)
        '2xl:grid-cols-7', // 2XL: 7 columns (1536px+)
      ],
      false: '',
    },
  },
  defaultVariants: {
    isResponsive: true,
  },
});

/** Grid props for a fixed column count, or the responsive classes when there is none. */
function getGridProps(columns?: number): Pick<React.HTMLAttributes<HTMLElement>, 'className' | 'style'> {
  if (columns === undefined) {
    return { className: gridClasses() };
  }

  return {
    className: gridClasses({ isResponsive: false }),
    style: { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` },
  };
}

const loadingCountClasses = cva({
  base: 'h-10 w-36 my-5',
});
const LoadingAssetGrid = React.memo(
  ({
    showCount = true,
    loadingCount = 12,
    className,
    columns,
  }: { showCount: boolean; loadingCount: number; className?: string; columns?: number }) => {
    return (
      <div className={cx(containerClasses(), className)}>
        {showCount && <Skeleton variant="square" className={loadingCountClasses()} />}
        <div {...getGridProps(columns)}>
          {Array.from({ length: loadingCount }, (_, index) => {
            const skeletonId = `asset-skeleton-${index}`;
            return (
              <Asset key={skeletonId} isLoading showScoreBar={false}>
                <Asset.Image name="Loading..." src={null} aria-hidden={true} />
              </Asset>
            );
          })}
        </div>
      </div>
    );
  },
);

const EmptyAssetResult = React.memo(({ children }: React.PropsWithChildren) => {
  return (
    <LibraryEmptyState
      heading="No assets found"
      description="No assets match your search, or there are no assets scored yet."
      icon={Image01Icon}
    >
      {children}
    </LibraryEmptyState>
  );
});

export const AssetGrid = React.forwardRef<HTMLDivElement, AssetGridProps>(
  (
    {
      children,
      totalCount,
      loadingCount = 12,
      className,
      countPrefix = '',
      isLoading = false,
      uploadAssetDialog,
      showCount = true,
      columns,
      ...other
    },
    ref,
  ) => {
    const childrenArray = React.Children.toArray(children);

    const isLoadingAssetGrid = isLoading && childrenArray.length === 0;
    const isEmptyResult = childrenArray.length === 0 && !isLoading;
    const hasAssets = childrenArray.length > 0 && !isLoading;

    const totalCountText = React.useMemo(() => {
      return totalCount ? new Number(totalCount).toLocaleString() : '0';
    }, [totalCount]);

    if (isEmptyResult) {
      return <EmptyAssetResult>{uploadAssetDialog}</EmptyAssetResult>;
    }

    if (isLoadingAssetGrid) {
      return (
        <LoadingAssetGrid showCount={showCount} loadingCount={loadingCount} className={className} columns={columns} />
      );
    }

    return (
      <div ref={ref} className={cx(containerClasses(), className)} {...other}>
        {hasAssets && showCount && (
          <Text
            showEllipsis={false}
            className={cx(loadingCountClasses(), 'min-w-fit')}
            size={TextSize.Sm}
            color={TextColor.Tertiary}
            weight={TextWeight.Medium}
          >
            {countPrefix && `${countPrefix} `}
            {totalCount > 1 ? `${totalCountText} assets` : `${totalCountText} asset`}
          </Text>
        )}

        <section
          aria-label={`Asset grid containing ${totalCountText} ${totalCount === 1 ? 'asset' : 'assets'}`}
          {...getGridProps(columns)}
        >
          {children}
        </section>
      </div>
    );
  },
);

AssetGrid.displayName = 'AssetGrid';

export type { AssetGridProps };
